import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { MainComponent } from '@modules/main/main.component';
import { LoginComponent } from '@modules/login/login.component';
import { RegisterComponent } from '@modules/register/register.component';
import { DashboardComponent } from '@pages/dashboard/dashboard.component';
import { AuthGuard } from '@guards/auth.guard';
import { NonAuthGuard } from '@guards/non-auth.guard';
import { ForgotPasswordComponent } from '@modules/forgot-password/forgot-password.component';
import { RecoverPasswordComponent } from '@modules/recover-password/recover-password.component';
import { PrivacyPolicyComponent } from '@modules/privacy-policy/privacy-policy.component';
import { AuthRolesGuard } from '@guards/auth-roles.guard';
import { ROUTE_ROLES } from './config/role-config';
import { UsersComponent as UsersPageComponent } from '@pages/admin/users/users.component';
import { UserDetailsComponent as DetailUserPageComponent } from '@pages/admin/users/details/user-details.component';
import { CompanyComponent } from '@pages/admin/company/company.component';
import { NewPracticeComponent } from '@pages/practices-management/practices/new-practice/new-practice.component';
import { NewUserComponent } from '@pages/admin/users/new-user/new-user.component';
import { StockComponent } from '@pages/stock-management/stock.component';
import { UsedVehicleComponent } from '@pages/used-vehicle-management/used-vehicle.component';
import { PracticeDetailsComponent } from '@pages/practices-management/practices/practice-details/practice-details.component';
import { BrandComponent } from '@pages/tables/brand/brand.component';
import { ActionPracticesComponent } from '@pages/tables/action-practices/action-practices.component';
import { BigBuyersComponent } from '@pages/tables/big-buyers/big-buyers.component';
import { RentalPracticesComponent } from '@pages/tables/rental-practices/rental-practices.component';
import { SellerComponent } from '@pages/tables/seller/seller.component';
import { ServicePracticesComponent } from '@pages/tables/service-practices/service-practices.component';
import { InsuranceServiceTypeComponent } from '@pages/tables/insurance-service-type/insurance-service-type.component';
import { RentalAgencyComponent } from '@pages/tables/rental-agency/rental-agency.component';
import { SubagentAgencyComponent } from '@pages/tables/subagent-agency/subagent-agency.component';
import { VehicleAccessoryComponent } from '@pages/tables/vehicle-accessory/vehicle-accessory.component';
import { ModelsComponent } from '@pages/tables/models/models.component';
import { CashMovementsComponent } from '@pages/cash-management/cash-movements/cash-movements.component';
import { MenuBiConfigComponent } from '@pages/tables/menu-bi-config/menu-bi-config.component';
import { MenuBiComponent } from '@pages/menu-bi/menu-bi.component';
import { PracticesComponent } from '@pages/practices-management/practices/practices.component';
import { DeliveryCardboardManagementComponent } from '@pages/practices-management/delivery-cardboard-management/delivery-cardboard-management.component';
import { NewCashMovementsComponent } from '@pages/cash-management/cash-movements/new-cash-movements/new-cash-movements.component';
import { PaymentTypeComponent } from '@pages/tables/payment-type/payment-type.component';
import { ContactSourceComponent } from '@pages/tables/contact-source/contact-source.component';
import { WarrantyComponent } from '@pages/tables/warranty/warranty.component';
import { BrandGoalsComponent } from '@pages/practices-management/brand-goals/brand-goals.component';
import { BusinessMachinesComponent } from '@pages/practices-management/business-machines/business-machines.component';
import { UsedVehicleDetailComponent } from '@pages/used-vehicle-management/details/used-vehicle-detail.component';
import { BankAccountComponent } from '@pages/tables/bank-account/bank-account.component';
import { BiggerDiscountComponent } from '@pages/bigger-discount/bigger-discount.component';
import { CrossF7TableComponent } from '@pages/imports/cross-f7-table/cross-f7-table.component';
import { CrossF8TableComponent } from '@pages/imports/cross-f8-table/cross-f8-table.component';
import { VwfsDashboardTableComponent } from '@pages/imports/vwfs-dashboard-table/vwfs-dashboard-table.component';
import { VwfsCommissionTableComponent } from '@pages/imports/vwfs-commission-table/vwfs-commission-table.component';
import { VwfsFrameTableComponent } from '@pages/imports/vwfs-frame-table/vwfs-frame-table.component';
import { ImportSummaryComponent } from '@pages/imports/import-summary/import-summary.component';
import { LoyaltyTableComponent } from '@pages/imports/loyalty-table/loyalty-table.component';
import { VwbankCommissionTableComponent } from '@pages/imports/vwbank-commission-table/vwbank-commission-table.component';
import { TableMSComponent } from '@pages/bigger-discount/table-ms/table-ms.component';
import { DocumentationMmssComponent } from '@pages/mmss-management/documentation-mmss/documentation-mmss.component';
import { PracticeDeletedComponent } from '@pages/imports/practice-deleted/practice-deleted.component';
import { CashMovementsAnalisisComponent } from '@pages/cash-management/cash-movements-analisis/cash-movements-analisis.component';
import { RentalPracticeListComponent } from '@pages/rental-management/rental-practice-list.component';
import { RentPracticeDetailComponent } from '@pages/rental-management/rent-practice-detail/rent-practice-detail.component';
import { RentalTimelineComponent } from '@pages/rental-management/rental-timeline/rental-timeline.component';
import { RentalReservationListComponent } from '@pages/rental-management/rental-reservation-list/rental-reservation-list.component';
import { CommissionComponent } from '@pages/tables/commission/commission.component';
import { VhmProFormaTableComponent } from '@pages/imports/vhm-pro-forma-table/vhm-pro-forma-table.component';
import { VhmReportTableComponent } from '@pages/imports/vhm-report-table/vhm-report-table.component';
import { OrderSliTableComponent } from '@pages/imports/order-sli-table/order-sli-table.component';
import { InsurancesPageListComponent } from '@pages/insurances/insurances-page-list.component';
import { InsuranceDetailComponent } from '@pages/insurances/insurance-detail/insurance-detail.component';
import { VehicleRegistryPageListComponent } from '@pages/vehicle-registry/vehicle-registry-page-list.component';
import { GaragePassagesComponent } from '@pages/garage-passages/garage-passages.component';
import { CustomersComponent } from '@pages/tables/customers/customers.component';
import { TestDrivePageListComponent } from '@pages/test-drive/test-drive-page-list.component';
import { TestDriveDetailComponent } from '@pages/test-drive/test-drive-detail/test-drive-detail.component';
import { CommissionSignalmanComponent } from '@pages/tables/commission-signalman/commission-signalman.component';

const routes: Routes = [
  {
    path: '',
    component: MainComponent,
    canActivate: [AuthGuard],
    canActivateChild: [AuthGuard],
    children: [
      {
        path: '',
        component: DashboardComponent,
      },
      {
        path: 'admin/users',
        component: UsersPageComponent,
        canActivate: [AuthRolesGuard],
        data: ROUTE_ROLES.GESTORE_UTENTI,
      },
      {
        path: 'admin/users/:username',
        component: DetailUserPageComponent,
        canActivate: [AuthRolesGuard],
        data: ROUTE_ROLES.GESTORE_UTENTI,
      },
      {
        path: 'admin/user/new',
        component: NewUserComponent,
        canActivate: [AuthRolesGuard],
        data: ROUTE_ROLES.GESTORE_UTENTI,
      },
      {
        path: 'admin/company',
        component: CompanyComponent,
        canActivate: [AuthRolesGuard],
        data: ROUTE_ROLES.ADMIN,
      },
      {
        path: 'practices-management/practices',
        component: PracticesComponent,
        canActivate: [AuthRolesGuard],
        data: ROUTE_ROLES.PRACTICES,
      },
      {
        path: 'rental-practices/list',
        component: RentalPracticeListComponent,
        canActivate: [AuthRolesGuard],
        data: ROUTE_ROLES.RENTAL_PRACTICES,
      },
      {
        path: 'insurances',
        component: InsurancesPageListComponent,
        canActivate: [AuthRolesGuard],
        data: ROUTE_ROLES.INSURANCES,
      },
      {
        path: 'insurances/detail-insurance/:id',
        component: InsuranceDetailComponent,
        canActivate: [AuthRolesGuard],
        data: ROUTE_ROLES.INSURANCES,
      },
      {
        path: 'vehicle-registry',
        component: VehicleRegistryPageListComponent,
        canActivate: [AuthRolesGuard],
        data: ROUTE_ROLES.VEHICLE_REGISTRY,
      },
      {
        path: 'customers',
        component: CustomersComponent,
        canActivate: [AuthRolesGuard],
        data: ROUTE_ROLES.CUSTOMERS,
      },
      {
        path: 'rental-practices/timeline',
        component: RentalTimelineComponent,
        canActivate: [AuthRolesGuard],
        data: ROUTE_ROLES.RENTAL_PRACTICES,
      },
      {
        path: 'rental-practices/reservations',
        component: RentalReservationListComponent,
        canActivate: [AuthRolesGuard],
        data: ROUTE_ROLES.RENTAL_PRACTICES,
      },
      {
        path: 'rental-practices/detail-practice/:id',
        component: RentPracticeDetailComponent,
        canActivate: [AuthRolesGuard],
        data: ROUTE_ROLES.RENTAL_PRACTICES,
      },
      {
        path: 'practices-management/business-machines',
        component: BusinessMachinesComponent,
        canActivate: [AuthRolesGuard],
        data: ROUTE_ROLES.BUSINESS_MACHINES,
      },
      {
        path: 'practices-management/practices/details/:practiceId',
        component: PracticeDetailsComponent,
        canActivate: [AuthRolesGuard],
        data: ROUTE_ROLES.PRACTICES,
      },
      {
        path: 'practices-management/practices/new',
        component: NewPracticeComponent,
        canActivate: [AuthRolesGuard],
        data: ROUTE_ROLES.PRACTICES,
      },
      {
        path: 'practices-management/delivery-cardboard',
        component: DeliveryCardboardManagementComponent,
        canActivate: [AuthRolesGuard],
        data: ROUTE_ROLES.DELIVERY_CARDBOARD,
      },
      {
        path: 'practices-management/brand-goals',
        component: BrandGoalsComponent,
        canActivate: [AuthRolesGuard],
        data: ROUTE_ROLES.BRAND_GOALS,
      },
      {
        path: 'stock-management',
        component: StockComponent,
        canActivate: [AuthRolesGuard],
        data: ROUTE_ROLES.STOCK,
      },
      {
        path: 'used-vehicle-management',
        component: UsedVehicleComponent,
        canActivate: [AuthRolesGuard],
        data: ROUTE_ROLES.USED_VEHICLE,
      },
      {
        path: 'used-vehicle-management/details/:id',
        component: UsedVehicleDetailComponent,
        canActivate: [AuthRolesGuard],
        data: ROUTE_ROLES.USED_VEHICLE,
      },
      {
        path: 'mmss-management/simulator-ms',
        component: BiggerDiscountComponent,
        canActivate: [AuthRolesGuard],
        data: ROUTE_ROLES.MMSS,
      },
      {
        path: 'mmss-management/table-ms',
        component: TableMSComponent,
        canActivate: [AuthRolesGuard],
        data: ROUTE_ROLES.MMSS,
      },
      {
        path: 'mmss-management/documentation-mmss',
        component: DocumentationMmssComponent,
        canActivate: [AuthRolesGuard],
        data: ROUTE_ROLES.MMSS,
      },
      {
        path: 'imports/crossf7',
        component: CrossF7TableComponent,
        canActivate: [AuthRolesGuard],
        data: ROUTE_ROLES.IMPORTS,
      },
      {
        path: 'imports/practice-deleted',
        component: PracticeDeletedComponent,
        canActivate: [AuthRolesGuard],
        data: ROUTE_ROLES.IMPORTS,
      },
      {
        path: 'imports/crossf8',
        component: CrossF8TableComponent,
        canActivate: [AuthRolesGuard],
        data: ROUTE_ROLES.IMPORTS,
      },
      {
        path: 'imports/loyalty',
        component: LoyaltyTableComponent,
        canActivate: [AuthRolesGuard],
        data: ROUTE_ROLES.IMPORTS,
      },
      {
        path: 'imports/vwbankCommission',
        component: VwbankCommissionTableComponent,
        canActivate: [AuthRolesGuard],
        data: ROUTE_ROLES.IMPORTS,
      },
      {
        path: 'imports/vwfsDashboard',
        component: VwfsDashboardTableComponent,
        canActivate: [AuthRolesGuard],
        data: ROUTE_ROLES.IMPORTS,
      },
      {
        path: 'imports/vwfsCommission',
        component: VwfsCommissionTableComponent,
        canActivate: [AuthRolesGuard],
        data: ROUTE_ROLES.IMPORTS,
      },
      {
        path: 'imports/vwfsFrame',
        component: VwfsFrameTableComponent,
        canActivate: [AuthRolesGuard],
        data: ROUTE_ROLES.IMPORTS,
      },
      {
        path: 'imports/vhmProForma',
        component: VhmProFormaTableComponent,
        canActivate: [AuthRolesGuard],
        data: ROUTE_ROLES.IMPORTS,
      },
      {
        path: 'imports/vhmReport',
        component: VhmReportTableComponent,
        canActivate: [AuthRolesGuard],
        data: ROUTE_ROLES.IMPORTS,
      },
      {
        path: 'imports/orderSli',
        component: OrderSliTableComponent,
        canActivate: [AuthRolesGuard],
        data: ROUTE_ROLES.IMPORTS,
      },
      {
        path: 'imports/summary',
        component: ImportSummaryComponent,
        canActivate: [AuthRolesGuard],
        data: ROUTE_ROLES.IMPORTS,
      },
      {
        path: 'cash-management/cash-movements',
        component: CashMovementsComponent,
        canActivate: [AuthRolesGuard],
        data: ROUTE_ROLES.CASH_MANAGEMENT,
      },
      {
        path: 'cash-management/cash-movements/new',
        component: NewCashMovementsComponent,
        canActivate: [AuthRolesGuard],
        data: ROUTE_ROLES.CASH_MANAGEMENT,
      },
      {
        path: 'cash-management/cash-movements/details/:id',
        component: NewCashMovementsComponent,
        canActivate: [AuthRolesGuard],
        data: ROUTE_ROLES.CASH_MANAGEMENT,
      },
      {
        path: 'cash-management/cash-movements-analisis',
        component: CashMovementsAnalisisComponent,
        canActivate: [AuthRolesGuard],
        data: ROUTE_ROLES.CASH_MOVEMENTS_ANALYSIS,
      },
      {
        path: 'menu-bi',
        component: MenuBiComponent,
        canActivate: [AuthRolesGuard],
        data: ROUTE_ROLES.MENU_BI,
      },
      {
        path: 'tables/action-practices',
        component: ActionPracticesComponent,
        canActivate: [AuthRolesGuard],
        data: ROUTE_ROLES.TABLES,
      },
      {
        path: 'tables/big-buyers',
        component: BigBuyersComponent,
        canActivate: [AuthRolesGuard],
        data: ROUTE_ROLES.TABLES,
      },
      {
        path: 'tables/commissions',
        component: CommissionComponent,
        canActivate: [AuthRolesGuard],
        data: ROUTE_ROLES.TABLES,
      },
      {
        path: 'tables/commissions-signalman',
        component: CommissionSignalmanComponent,
        canActivate: [AuthRolesGuard],
        data: ROUTE_ROLES.TABLES,
      },
      {
        path: 'tables/brand',
        component: BrandComponent,
        canActivate: [AuthRolesGuard],
        data: ROUTE_ROLES.TABLES,
      },
      {
        path: 'tables/insurance-service-type',
        component: InsuranceServiceTypeComponent,
        canActivate: [AuthRolesGuard],
        data: ROUTE_ROLES.TABLES,
      },
      {
        path: 'tables/rental-practices',
        component: RentalPracticesComponent,
        canActivate: [AuthRolesGuard],
        data: ROUTE_ROLES.TABLES,
      },
      {
        path: 'tables/subagent-agency',
        component: SubagentAgencyComponent,
        canActivate: [AuthRolesGuard],
        data: ROUTE_ROLES.TABLES,
      },
      {
        path: 'tables/seller',
        component: SellerComponent,
        canActivate: [AuthRolesGuard],
        data: ROUTE_ROLES.TABLES,
      },
      {
        path: 'tables/service-practices',
        component: ServicePracticesComponent,
        canActivate: [AuthRolesGuard],
        data: ROUTE_ROLES.TABLES,
      },
      {
        path: 'tables/rental-agency',
        component: RentalAgencyComponent,
        canActivate: [AuthRolesGuard],
        data: ROUTE_ROLES.TABLES,
      },
      {
        path: 'tables/vehicle-accessory',
        component: VehicleAccessoryComponent,
        canActivate: [AuthRolesGuard],
        data: ROUTE_ROLES.TABLES,
      },
      {
        path: 'tables/models',
        component: ModelsComponent,
        canActivate: [AuthRolesGuard],
        data: ROUTE_ROLES.TABLES,
      },
      {
        path: 'tables/payment-type',
        component: PaymentTypeComponent,
        canActivate: [AuthRolesGuard],
        data: ROUTE_ROLES.TABLES,
      },
      {
        path: 'tables/contact-source',
        component: ContactSourceComponent,
        canActivate: [AuthRolesGuard],
        data: ROUTE_ROLES.TABLES,
      },
      {
        path: 'tables/warranty',
        component: WarrantyComponent,
        canActivate: [AuthRolesGuard],
        data: ROUTE_ROLES.TABLES,
      },
      {
        path: 'tables/bank-account',
        component: BankAccountComponent,
        canActivate: [AuthRolesGuard],
        data: ROUTE_ROLES.TABLES,
      },
      {
        path: 'tables/menu-bi-config',
        component: MenuBiConfigComponent,
        canActivate: [AuthRolesGuard],
        data: ROUTE_ROLES.TABLES,
      },
      {
        path: 'garage-passages',
        component: GaragePassagesComponent,
        canActivate: [AuthRolesGuard],
        data: ROUTE_ROLES.GESTIONE_CEM,
      },
      {
        path: 'test-drive',
        component: TestDrivePageListComponent,
        canActivate: [AuthRolesGuard],
        data: ROUTE_ROLES.TEST_DRIVE,
      },
      {
        path: 'test-drive/new',
        component: TestDriveDetailComponent,
        canActivate: [AuthRolesGuard],
        data: ROUTE_ROLES.TEST_DRIVE,
      },
      {
        path: 'test-drive/detail/:id',
        component: TestDriveDetailComponent,
        canActivate: [AuthRolesGuard],
        data: ROUTE_ROLES.TEST_DRIVE,
      },
    ],
  },
  {
    path: 'login',
    component: LoginComponent,
    canActivate: [NonAuthGuard],
  },
  {
    path: 'register',
    component: RegisterComponent,
    canActivate: [NonAuthGuard],
  },
  {
    path: 'forgot-password',
    component: ForgotPasswordComponent,
    canActivate: [NonAuthGuard],
  },
  {
    path: 'recover-password',
    component: RecoverPasswordComponent,
    canActivate: [NonAuthGuard],
  },
  {
    path: 'privacy-policy',
    component: PrivacyPolicyComponent,
    canActivate: [NonAuthGuard],
  },
  { path: '**', redirectTo: '' },
];

@NgModule({
  imports: [RouterModule.forRoot(routes, { relativeLinkResolution: 'legacy' })],
  exports: [RouterModule],
})
export class AppRoutingModule {}
